<template>
  <div class="panel-paradas">
    <div class="cabecera">
      <div>
        <div class="seccion-label">
          Paradas
        </div>
        <p class="ayuda">
          El orden es el del recorrido. Todas las paradas de una ruta son del
          mismo tipo: empresas, lugares o productos.
        </p>
      </div>
      <v-btn
        color="primary"
        size="small"
        variant="flat"
        @click="abrirDialogo()"
      >
        <v-icon start>
          mdi-plus
        </v-icon>
        Agregar
      </v-btn>
    </div>

    <div
      v-if="cargando"
      class="estado"
    >
      <v-progress-circular
        indeterminate
        color="primary"
        size="26"
        width="3"
      />
      <span>Cargando paradas...</span>
    </div>

    <div
      v-else-if="paradas.length === 0"
      class="estado"
    >
      <v-icon
        size="34"
        color="grey"
      >
        mdi-map-marker-path
      </v-icon>
      <span>Esta ruta todavía no tiene paradas.</span>
    </div>

    <div
      v-else
      class="lista"
    >
      <div
        v-for="(parada, indice) in paradas"
        :key="parada.idStop"
        class="parada"
        :class="{ 'parada-oculta': !parada.visible }"
      >
        <div class="columna-orden">
          <v-btn
            icon
            variant="text"
            size="x-small"
            title="Subir"
            :disabled="indice === 0 || reordenando"
            @click="mover(indice, -1)"
          >
            <v-icon size="16">
              mdi-chevron-up
            </v-icon>
          </v-btn>

          <div class="orden">
            {{ parada.order }}
          </div>

          <v-btn
            icon
            variant="text"
            size="x-small"
            title="Bajar"
            :disabled="indice === paradas.length - 1 || reordenando"
            @click="mover(indice, 1)"
          >
            <v-icon size="16">
              mdi-chevron-down
            </v-icon>
          </v-btn>
        </div>

        <div class="cuerpo">
          <div class="titulo">
            <v-icon size="15">
              {{ iconoDe(parada.targetType) }}
            </v-icon>
            {{ parada.targetName }}
          </div>
          <div
            v-if="parada.companyName || parada.city"
            class="ciudad"
          >
            <template v-if="parada.companyName">{{ parada.companyName }}</template>
            <template v-if="parada.companyName && parada.city"> · </template>
            <template v-if="parada.city">{{ parada.city }}</template>
          </div>
          <p
            v-if="parada.note"
            class="nota"
          >
            {{ parada.note }}
          </p>

          <div class="etiquetas">
            <span
              v-if="parada.price"
              class="etiqueta etiqueta-precio"
            >
              {{ enPesos(parada.price) }}
            </span>
            <span
              v-if="parada.estimatedMinutes"
              class="etiqueta"
            >
              {{ parada.estimatedMinutes }} min
            </span>
            <!-- La regla del negocio, visible: sin hospedaje vigente la parada
                 no se muestra al publico, pero la ruta sigue en pie. -->
            <span
              v-if="!parada.visible"
              class="etiqueta etiqueta-oculta"
            >
              <v-icon size="12">mdi-eye-off-outline</v-icon>
              No se está viendo
            </span>
          </div>
        </div>

        <div class="acciones">
          <v-btn
            icon
            variant="text"
            size="small"
            title="Editar"
            @click="abrirDialogo(parada)"
          >
            <v-icon size="18">
              mdi-pencil
            </v-icon>
          </v-btn>
          <v-btn
            icon
            variant="text"
            size="small"
            title="Eliminar"
            @click="pedirConfirmacion(parada)"
          >
            <v-icon size="18">
              mdi-delete
            </v-icon>
          </v-btn>
        </div>
      </div>
    </div>

    <!-- ALTA Y EDICION -->
    <v-dialog
      v-model="dialogo"
      max-width="600"
    >
      <v-card class="modal">
        <div class="modal-cabecera">
          <div>
            <div class="modal-etiqueta">
              <v-icon size="12">
                mdi-map-marker-path
              </v-icon>
              {{ form.idStop ? 'Editando' : 'Nueva' }}
            </div>
            <h2 class="modal-titulo">
              {{ form.idStop ? 'Editar parada' : 'Agregar parada' }}
            </h2>
          </div>
          <v-btn
            icon
            variant="text"
            size="small"
            class="modal-cerrar"
            @click="dialogo = false"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <v-card-text class="modal-cuerpo">
          <!-- El destino solo se elige al crear: cambiarlo al editar es en
               realidad otra parada, y deja la nota hablando de otro sitio. -->
          <template v-if="!form.idStop">
            <div class="modal-grupo">
              Qué es esta parada
            </div>
            <v-btn-toggle
              v-model="tipoDestino"
              color="primary"
              density="compact"
              mandatory
              class="mb-1"
            >
              <v-btn
                value="empresa"
                :disabled="tipoBloqueado && tipoBloqueado !== 'empresa'"
              >
                <v-icon start>
                  mdi-storefront-outline
                </v-icon>
                Empresa
              </v-btn>
              <v-btn
                value="lugar"
                :disabled="tipoBloqueado && tipoBloqueado !== 'lugar'"
              >
                <v-icon start>
                  mdi-map-marker-outline
                </v-icon>
                Lugar
              </v-btn>
              <v-btn
                value="producto"
                :disabled="tipoBloqueado && tipoBloqueado !== 'producto'"
              >
                <v-icon start>
                  mdi-food
                </v-icon>
                Producto
              </v-btn>
            </v-btn-toggle>

            <!-- El bloqueo se explica, no solo se aplica: un boton apagado sin
                 motivo se lee como que algo esta roto. -->
            <p
              v-if="tipoBloqueado"
              class="modal-ayuda mb-3"
            >
              Esta ruta es de {{ tipoBloqueado }}, así que todas sus paradas tienen
              que serlo. Para cambiar de tipo hay que quitar antes las paradas que ya tiene.
            </p>

            <v-autocomplete
              v-model="destinoElegido"
              :items="opcionesDestino"
              :loading="buscandoDestino"
              :search="textoDestino"
              item-title="nombre"
              item-value="id"
              class="fg-blue"
              :label="etiquetaBuscador"
              variant="outlined"
              density="compact"
              no-filter
              hide-no-data
              return-object
              @update:search="buscarDestino"
            />
          </template>

          <div
            v-else
            class="destino-fijo"
          >
            <v-icon size="16">
              {{ iconoDe(form.targetType) }}
            </v-icon>
            <strong>{{ form.targetName }}</strong>
          </div>

          <div class="modal-grupo mt-5">
            Detalle
          </div>
          <v-textarea
            v-model="form.note"
            class="fg-blue"
            label="Nota"
            variant="outlined"
            density="compact"
            rows="2"
            auto-grow
            maxlength="500"
            hint="Por qué está esta parada aquí. Ej: pide el ajiaco"
            persistent-hint
          />
          <v-text-field
            v-model="form.estimatedMinutes"
            class="fg-blue mt-3"
            label="Minutos estimados"
            variant="outlined"
            density="compact"
            type="number"
            inputmode="numeric"
            min="0"
          />

          <v-alert
            v-if="errorForm"
            type="error"
            variant="tonal"
            density="comfortable"
            class="mt-4"
          >
            {{ errorForm }}
          </v-alert>
        </v-card-text>

        <v-card-actions class="modal-pie">
          <v-spacer />
          <v-btn
            variant="text"
            :disabled="guardando"
            @click="dialogo = false"
          >
            Cancelar
          </v-btn>
          <v-btn
            color="primary"
            variant="flat"
            :loading="guardando"
            @click="guardar"
          >
            Guardar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- CONFIRMAR BORRADO -->
    <v-dialog
      v-model="dialogoEliminar"
      max-width="420"
      :persistent="eliminando"
    >
      <v-card class="modal modal-confirmar">
        <div class="modal-confirmar-cabecera">
          <div class="modal-confirmar-icono">
            <v-icon size="20">
              mdi-alert-outline
            </v-icon>
          </div>
          <h2 class="modal-confirmar-titulo">
            Eliminar parada
          </h2>
        </div>
        <v-card-text class="modal-confirmar-cuerpo">
          <p v-if="porEliminar">
            Se va a quitar <strong>{{ porEliminar.targetName }}</strong> de esta ruta.
          </p>
          <p class="modal-irreversible">
            Esta acción no se puede deshacer.
          </p>
        </v-card-text>
        <v-card-actions class="modal-confirmar-pie">
          <v-spacer />
          <v-btn
            variant="text"
            :disabled="eliminando"
            @click="dialogoEliminar = false"
          >
            Cancelar
          </v-btn>
          <v-btn
            color="red"
            variant="flat"
            :loading="eliminando"
            @click="eliminar"
          >
            Eliminar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar
      v-model="aviso"
      :timeout="colorAviso === 'error' ? -1 : 4000"
      :color="colorAviso"
      location="top"
    >
      {{ mensajeAviso }}
      <template #actions>
        <v-btn
          variant="text"
          size="small"
          @click="aviso = false"
        >
          Cerrar
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import apisRutas from '@/apis/apisRutas'
import apisCompanys from '@/apis/apisCompanys'
import apisSites from '@/apis/apisSites'
import apisProducts from '@/apis/apisProducts'
import { mensajeDeError } from '@/utils/errores'
import { enPesos } from '@/utils/configuracion'

/**
 * Paradas de una ruta, para el administrador.
 *
 * Recibe solo el id de la ruta y carga lo suyo, como las tarjetas de empresa y
 * producto: asi el panel de rutas no tiene que saber nada de paradas.
 */
export default {
  name: 'PanelParadasRuta',
  props: {
    idRuta: {
      type: Number,
      required: true
    }
  },
  data() {
    return {
      paradas: [],
      cargando: true,

      dialogo: false,
      form: this.formVacio(),
      tipoDestino: 'empresa',
      textoDestino: '',
      opcionesDestino: [],
      destinoElegido: null,
      buscandoDestino: false,
      guardando: false,
      errorForm: null,

      dialogoEliminar: false,
      porEliminar: null,
      eliminando: false,

      // Bloquea las flechas mientras se guarda el orden, para que dos clics
      // seguidos no manden dos listas distintas y gane la que llegue ultima
      reordenando: false,

      aviso: false,
      mensajeAviso: '',
      colorAviso: 'success',

      // Para no lanzar una peticion por cada tecla del autocompletado
      temporizadorBusqueda: null
    }
  },
  computed: {
    /**
     * El tipo que la ruta ya tiene, o null si esta vacia.
     *
     * Sale de las paradas cargadas y no de un campo de la ruta: el backend
     * valida igual, esto solo evita ofrecer un tipo que va a ser rechazado.
     */
    tipoBloqueado() {
      return this.paradas.length > 0 ? this.paradas[0].targetType : null
    },
    etiquetaBuscador() {
      if (this.tipoDestino === 'empresa') {
        return 'Busca la empresa'
      }
      if (this.tipoDestino === 'producto') {
        return 'Busca el producto'
      }
      return 'Busca el lugar'
    }
  },
  watch: {
    idRuta() {
      this.cargar()
    },
    // Cambiar de empresa a lugar deja las opciones anteriores sin sentido
    tipoDestino() {
      this.opcionesDestino = []
      this.destinoElegido = null
      this.buscarDestino('')
    }
  },
  created() {
    this.cargar()
  },
  beforeUnmount() {
    clearTimeout(this.temporizadorBusqueda)
  },
  methods: {
    formVacio() {
      return {
        idStop: null,
        note: '',
        estimatedMinutes: null,
        targetType: null,
        targetName: null
      }
    },
    cargar() {
      this.cargando = true

      apisRutas.paradasDeRuta(this.idRuta)
        .then(paradas => {
          this.paradas = paradas || []
        })
        .catch(error => {
          this.avisar(mensajeDeError(error, 'No se pudieron cargar las paradas'), 'error')
        })
        .finally(() => {
          this.cargando = false
        })
    },
    /**
     * Sube o baja una parada un puesto y guarda el orden completo.
     *
     * Flechas y no arrastrar y soltar: este panel tambien se abre en celular,
     * y el arrastre nativo del navegador no responde al dedo. Con una libreria
     * de arrastre tactil se podria, pero para listas de cinco o seis paradas
     * dos botones hacen el trabajo sin sumar una dependencia.
     *
     * El movimiento se pinta al instante y se manda la lista entera. Si el
     * servidor lo rechaza se recarga desde la base, porque lo que el usuario
     * tiene que ver es el orden guardado y no el que quiso dejar.
     */
    mover(indice, salto) {
      const destino = indice + salto

      if (this.reordenando || destino < 0 || destino >= this.paradas.length) {
        return
      }

      const movidas = [...this.paradas]
      const [parada] = movidas.splice(indice, 1)
      movidas.splice(destino, 0, parada)

      this.paradas = movidas
      this.reordenando = true

      apisRutas.reordenarParadas(this.idRuta, movidas.map(p => p.idStop))
        .then(guardadas => {
          this.paradas = guardadas || []
        })
        .catch(error => {
          this.avisar(mensajeDeError(error, 'No se pudo guardar el orden'), 'error')
          this.cargar()
        })
        .finally(() => {
          this.reordenando = false
        })
    },
    iconoDe(tipo) {
      if (tipo === 'empresa') {
        return 'mdi-storefront-outline'
      }
      if (tipo === 'producto') {
        return 'mdi-food'
      }
      return 'mdi-map-marker-outline'
    },
    enPesos,
    abrirDialogo(parada = null) {
      this.errorForm = null
      this.destinoElegido = null
      this.textoDestino = ''
      this.opcionesDestino = []

      if (parada) {
        this.form = {
          idStop: parada.idStop,
          note: parada.note || '',
          estimatedMinutes: parada.estimatedMinutes,
          targetType: parada.targetType,
          targetName: parada.targetName
        }
      } else {
        this.form = this.formVacio()
        // Si la ruta ya tiene tipo se arranca en ese: es el unico elegible
        this.tipoDestino = this.tipoBloqueado || 'empresa'
        this.buscarDestino('')
      }

      this.dialogo = true
    },
    buscarDestino(texto) {
      this.textoDestino = texto || ''
      clearTimeout(this.temporizadorBusqueda)

      this.temporizadorBusqueda = setTimeout(() => {
        this.buscandoDestino = true

        let peticion

        if (this.tipoDestino === 'empresa') {
          peticion = apisCompanys.searchAll(this.textoDestino, 0)
            .then(r => (r.content || []).map(e => ({ id: e.idCompany, nombre: e.company })))
        } else if (this.tipoDestino === 'producto') {
          // Con la empresa al lado: doce hamburguesas del festival se llaman
          // parecido y por el nombre solo no hay como distinguirlas
          peticion = apisProducts.search(this.textoDestino, '', '', 0)
            .then(r => (r.content || []).map(p => ({
              id: p.idProduct,
              nombre: p.company ? `${p.product} — ${p.company.company}` : p.product
            })))
        } else {
          peticion = apisSites.search({ page: 0, text: this.textoDestino || null })
            .then(r => (r.content || []).map(l => ({ id: l.idSite, nombre: l.name })))
        }

        peticion
          .then(opciones => {
            this.opcionesDestino = opciones
          })
          .catch(() => {
            this.opcionesDestino = []
          })
          .finally(() => {
            this.buscandoDestino = false
          })
      }, 300)
    },
    guardar() {
      this.errorForm = null

      if (!this.form.idStop && !this.destinoElegido) {
        this.errorForm = 'Elige la empresa o el lugar de esta parada'
        return
      }

      const cuerpo = {
        idStop: this.form.idStop,
        idRoute: this.idRuta,
        note: this.form.note ? this.form.note.trim() : null,
        estimatedMinutes: this.form.estimatedMinutes === '' || this.form.estimatedMinutes === null
          ? null
          : Number(this.form.estimatedMinutes)
      }

      if (!this.form.idStop) {
        // Uno solo de los tres, nunca dos: es lo que exige el CHECK
        if (this.tipoDestino === 'empresa') {
          cuerpo.idCompany = this.destinoElegido.id
        } else if (this.tipoDestino === 'producto') {
          cuerpo.idProduct = this.destinoElegido.id
        } else {
          cuerpo.idSite = this.destinoElegido.id
        }
      }

      this.guardando = true

      apisRutas.guardarParada(cuerpo)
        .then(() => {
          this.dialogo = false
          this.avisar('Parada guardada', 'success')
          this.cargar()
        })
        .catch(error => {
          this.errorForm = mensajeDeError(error, 'No se pudo guardar la parada')
        })
        .finally(() => {
          this.guardando = false
        })
    },
    pedirConfirmacion(parada) {
      this.porEliminar = parada
      this.dialogoEliminar = true
    },
    eliminar() {
      if (!this.porEliminar) {
        return
      }

      this.eliminando = true

      apisRutas.eliminarParada(this.porEliminar.idStop)
        .then(() => {
          this.dialogoEliminar = false
          this.avisar('Parada eliminada', 'success')
          this.cargar()
        })
        .catch(error => {
          this.avisar(mensajeDeError(error, 'No se pudo eliminar la parada'), 'error')
        })
        .finally(() => {
          this.eliminando = false
          this.porEliminar = null
        })
    },
    avisar(mensaje, color) {
      this.mensajeAviso = mensaje
      this.colorAviso = color
      this.aviso = true
    }
  }
}
</script>

<style scoped>
.cabecera {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
}
.seccion-label {
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #1976d2;
}
.ayuda {
  margin: 4px 0 0;
  font-size: 0.78rem;
  color: #98a2b3;
}

.lista {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.parada {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px;
  border: 1px solid #e4e7ec;
  border-radius: 10px;
  background: #fff;
}
/* Apagada, no escondida: el administrador tiene que ver justo la que el
   publico no esta viendo. */
.parada-oculta {
  background: #fafbfc;
  border-style: dashed;
}
.parada-oculta .titulo {
  color: #98a2b3;
}

/* Las flechas y el numero en una sola columna, para que el bloque de
   reordenar se lea como una pieza y no como botones sueltos junto al texto */
.columna-orden {
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.orden {
  flex: 0 0 auto;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: #e8f1fb;
  color: #1976d2;
  font-size: 0.78rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
}
.cuerpo {
  flex: 1 1 auto;
  min-width: 0;
}
.titulo {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 0.94rem;
  font-weight: 600;
  color: #1a1a1a;
}
.ciudad {
  font-size: 0.76rem;
  color: #98a2b3;
}
.nota {
  margin: 5px 0 0;
  font-size: 0.82rem;
  color: #667085;
  line-height: 1.4;
}
.etiquetas {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  margin-top: 6px;
}
.etiqueta {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 1px 7px;
  border-radius: 9px;
  background: #f2f5f9;
  color: #475467;
  font-size: 0.7rem;
  font-weight: 600;
}
.etiqueta-precio {
  background: #e6f5ee;
  color: #0f7a52;
}
.etiqueta-oculta {
  background: #fdeceb;
  color: #a8271f;
}
.acciones {
  flex: 0 0 auto;
  display: flex;
}

.destino-fijo {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 12px;
  border-radius: 8px;
  background: #f2f5f9;
  font-size: 0.9rem;
  color: #475467;
}

.estado {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 36px 12px;
  color: #667085;
  font-size: 0.86rem;
  text-align: center;
}
</style>
